import { useRef, useEffect, useState } from 'react';
import confetti from 'canvas-confetti';
import './App.css';

interface Obstacle {
  x: number;
  y: number;
  width: number;
  height: number;
  isPink?: boolean;
}

interface Bubble {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface Explosion {
  x: number;
  y: number;
  particles: Array<{
    x: number;
    y: number;
    vx: number;
    vy: number;
    life: number;
    maxLife: number;
  }>;
}


const leaderboardUrl = "https://wpk8b7.buildship.run/maia-racing-scores";

function App() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [gameStarted, setGameStarted] = useState(false);
  const [carPosition, setCarPosition] = useState({ x: 375, y: 500 });
  const [backgroundY, setBackgroundY] = useState(0);
  const [obstacles, setObstacles] = useState<Obstacle[]>([]);
  const [lives, setLives] = useState(3);
  const [explosions, setExplosions] = useState<Explosion[]>([]);
  const [canvasSize, setCanvasSize] = useState({ width: 800, height: 600 });
  const [score, setScore] = useState(0);
  const [playerName, setPlayerName] = useState('');
  const [isSubmittingScore, setIsSubmittingScore] = useState(false);
  const [leaderboard, setLeaderboard] = useState<Array<{Name: string, Score: number}>>([]);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [isLoadingLeaderboard, setIsLoadingLeaderboard] = useState(false);
  const [scoreSubmitted, setScoreSubmitted] = useState(false);
  const [level, setLevel] = useState(1);
  const gameStartTimeRef = useRef<number>(0);
  const [isNewHighScore, setIsNewHighScore] = useState(false);
  const [bubbles, setBubbles] = useState<Bubble[]>([]);
  const [collectedBubbles, setCollectedBubbles] = useState(0);
  const [bubbleShieldActive, setBubbleShieldActive] = useState(false);
  const bubbleShieldTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [bubblesSpawnedThisLevel, setBubblesSpawnedThisLevel] = useState(0);
  const lastBubbleSpawnTimeRef = useRef<number>(0);
  const [showInstructions, setShowInstructions] = useState(false);
  const [instructionsOpenedWithTouch, setInstructionsOpenedWithTouch] = useState(false);
  
  const isMobile = () => window.innerWidth <= 768;
  
  const isMobileDevice = () => {
    const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
    return /android|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
  };

  const createConfetti = () => {
    confetti({
      angle: Math.random() * 70 + 55,
      spread: Math.random() * 20 + 50,
      particleCount: Math.random() * 50 + 50,
      origin: { y: 0.6 }
    });
  };
  
  const handleStart = () => {
    // Set canvas size based on screen
    if (isMobile()) {
      setCanvasSize({ 
        width: window.innerWidth, 
        height: window.innerHeight 
      });
    } else {
      setCanvasSize({ width: 800, height: 600 });
    }
    
    setGameStarted(true);
    setLives(3);
    setObstacles([]);
    setCarPosition({ 
      x: isMobile() ? window.innerWidth / 2 - 73 : 375, 
      y: isMobile() ? window.innerHeight - 300 : 500 
    });
    setBackgroundY(0);
    setExplosions([]);
    setScore(0);
    setPlayerName('');
    setIsSubmittingScore(false);
    setShowLeaderboard(false);
    setLeaderboard([]);
    setScoreSubmitted(false);
    setLevel(1);
    gameStartTimeRef.current = Date.now();
    setIsNewHighScore(false);
    setBubbles([]);
    setCollectedBubbles(0);
    setBubbleShieldActive(false);
    setBubblesSpawnedThisLevel(0);
    lastBubbleSpawnTimeRef.current = 0;
    if (bubbleShieldTimeoutRef.current) {
      clearTimeout(bubbleShieldTimeoutRef.current);
      bubbleShieldTimeoutRef.current = null;
    }
  };

  const fetchLeaderboard = async () => {
    setIsLoadingLeaderboard(true);
    try {
      const response = await fetch(leaderboardUrl);
      if (response.ok) {
        const data = await response.json();
        setLeaderboard(data.sort((a: any, b: any) => b.score - a.score));
        setShowLeaderboard(true);
      } else {
        alert('Failed to load leaderboard. Please try again.');
      }
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
      alert('Failed to load leaderboard. Please check your connection and try again.');
    } finally {
      setIsLoadingLeaderboard(false);
    }
  };

  const submitScore = async () => {
    if (!playerName.trim()) {
      alert('Please enter your name to submit your score!');
      return;
    }

    setIsSubmittingScore(true);
    try {
      const response = await fetch(leaderboardUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: playerName.trim(),
          score: score
        })
      });

      if (response.ok) {
        const result = await response.json();
        setScoreSubmitted(true);
        
        // Check if this is a new high score
        if (result.state === 'new' || result.state === 'updated') {
          setIsNewHighScore(true);
          createConfetti();
        }
        
        await fetchLeaderboard();
      } else {
        alert('Failed to submit score. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting score:', error);
      alert('Failed to submit score. Please check your connection and try again.');
    } finally {
      setIsSubmittingScore(false);
    }
  };

  useEffect(() => {
    if (!gameStarted) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const carImage = new Image();
    carImage.src = '/just-car.png';

    // Scale based on screen size
    const scale = isMobile() ? Math.min(canvasSize.width, canvasSize.height) / 600 : 1;
    const carWidth = Math.floor(146 * scale);
    const carHeight = Math.floor(244 * scale);
    
    const capybaraImage = new Image();
    capybaraImage.src = '/just-capybara.png';
    
    const pinkCapybaraImage = new Image();
    pinkCapybaraImage.src = '/pink-capybara.png';
    
    const bubbleImage = new Image();
    bubbleImage.src = '/empty-bubble.png';

    const moveDelta = Math.floor(30 * scale);

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Draw scrolling background
      ctx.fillStyle = '#84f5d5';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Draw road lines
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 4;
      for (let i = 0; i < 6; i++) {
        const lineY = (backgroundY + i * 120) % 720;
        ctx.beginPath();
        ctx.moveTo(canvas.width / 2, lineY);
        ctx.lineTo(canvas.width / 2, lineY + 60);
        ctx.stroke();
      }
      
      // Draw bubbles
      bubbles.forEach(bubble => {
        if (bubbleImage.complete) {
          ctx.drawImage(bubbleImage, bubble.x, bubble.y, bubble.width, bubble.height);
        }
      });
      
      // Draw obstacles
      obstacles.forEach(obstacle => {
        const imageToUse = obstacle.isPink ? pinkCapybaraImage : capybaraImage;
        if (imageToUse.complete) {
          ctx.drawImage(imageToUse, obstacle.x, obstacle.y, obstacle.width, obstacle.height);
        }
      });
      
      // Draw car
      if (carImage.complete) {
        ctx.drawImage(carImage, carPosition.x, carPosition.y, carWidth, carHeight);
      }
      
      // Draw bubble shield around car if active
      if (bubbleShieldActive) {
        const shieldRadius = Math.max(carWidth, carHeight) * 0.7;
        const centerX = carPosition.x + carWidth / 2;
        const centerY = carPosition.y + carHeight / 2;
        
        ctx.strokeStyle = '#40E0D0';
        ctx.lineWidth = 4;
        ctx.globalAlpha = 0.7;
        ctx.beginPath();
        ctx.arc(centerX, centerY, shieldRadius, 0, Math.PI * 2);
        ctx.stroke();
        ctx.globalAlpha = 1;
      }
      
      // Draw explosions
      explosions.forEach(explosion => {
        explosion.particles.forEach(particle => {
          const alpha = particle.life / particle.maxLife;
          ctx.fillStyle = `rgba(255, ${Math.floor(100 * alpha)}, 0, ${alpha})`;
          ctx.beginPath();
          ctx.arc(particle.x, particle.y, 3 * alpha, 0, Math.PI * 2);
          ctx.fill();
        });
      });
      
      // Draw score and level in top left
      const scoreSize = Math.floor(30 * scale);
      ctx.fillStyle = '#000000';
      ctx.font = `${scoreSize}px Arial`;
      ctx.fillText(`Score: ${score}`, Math.floor(20 * scale), Math.floor(40 * scale));
      ctx.fillText(`Level: ${level}`, Math.floor(20 * scale), Math.floor(80 * scale));
      
      // Draw bubble count and clickable bubble icon
      if (collectedBubbles > 0) {
        const bubbleIconSize = Math.floor(40 * scale);
        const bubbleIconX = Math.floor(20 * scale);
        const bubbleIconY = Math.floor(140 * scale);
        
        // Draw bubble icon (clickable)
        if (bubbleImage.complete) {
          ctx.drawImage(bubbleImage, bubbleIconX, bubbleIconY - bubbleIconSize, bubbleIconSize, bubbleIconSize);
        }
        
        // Draw bubble count
        ctx.fillStyle = '#000000';
        ctx.font = `${Math.floor(20 * scale)}px Arial`;
        ctx.fillText(`x${collectedBubbles}`, bubbleIconX + bubbleIconSize + Math.floor(10 * scale), bubbleIconY - Math.floor(10 * scale));
      }
      
      // Draw hearts (lives) in top right
      const heartSize = Math.floor(40 * scale);
      const heartSpacing = Math.floor(60 * scale);
      for (let i = 0; i < lives; i++) {
        const heartX = canvas.width - Math.floor(50 * scale) - (i * heartSpacing);
        const heartY = Math.floor(40 * scale);
        
        ctx.fillStyle = '#ff0000';
        ctx.font = `${heartSize}px Arial`;
        ctx.fillText('♥', heartX, heartY);
      }
    };

    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === ' ' || e.key === 'Spacebar') {
        // Activate bubble shield with spacebar
        if (collectedBubbles > 0 && !bubbleShieldActive) {
          setCollectedBubbles(prev => prev - 1);
          setBubbleShieldActive(true);
          
          // Set 10 second timer
          if (bubbleShieldTimeoutRef.current) {
            clearTimeout(bubbleShieldTimeoutRef.current);
          }
          bubbleShieldTimeoutRef.current = setTimeout(() => {
            setBubbleShieldActive(false);
            bubbleShieldTimeoutRef.current = null;
          }, 10000);
        }
        return;
      }
      
      setCarPosition(prev => {
        let newX = prev.x;
        let newY = prev.y;
        
        switch(e.key) {
          case 'ArrowLeft':
            newX = Math.max(0, prev.x - moveDelta);
            break;
          case 'ArrowRight':
            newX = Math.min(canvas.width - carWidth, prev.x + moveDelta);
            break;
          case 'ArrowUp':
            newY = Math.max(0, prev.y - moveDelta);
            break;
          case 'ArrowDown':
            newY = Math.min(canvas.height - carHeight, prev.y + moveDelta);
            break;
        }
        
        return { x: newX, y: newY };
      });
    };

    const handleTouch = (e: TouchEvent) => {
      e.preventDefault();
      const canvas = canvasRef.current;
      if (!canvas) return;
      
      const rect = canvas.getBoundingClientRect();
      const touch = e.touches[0];
      const touchX = touch.clientX - rect.left;
      const touchY = touch.clientY - rect.top;
      
      // Check if touch is on bubble icon
      if (collectedBubbles > 0 && !bubbleShieldActive) {
        const bubbleIconSize = Math.floor(40 * scale);
        const bubbleIconX = Math.floor(20 * scale);
        const bubbleIconY = Math.floor(140 * scale);
        
        if (touchX >= bubbleIconX && touchX <= bubbleIconX + bubbleIconSize &&
            touchY >= bubbleIconY - bubbleIconSize && touchY <= bubbleIconY) {
          // Activate bubble shield
          setCollectedBubbles(prev => prev - 1);
          setBubbleShieldActive(true);
          
          // Set 10 second timer
          if (bubbleShieldTimeoutRef.current) {
            clearTimeout(bubbleShieldTimeoutRef.current);
          }
          bubbleShieldTimeoutRef.current = setTimeout(() => {
            setBubbleShieldActive(false);
            bubbleShieldTimeoutRef.current = null;
          }, 10000);
          
          return;
        }
      }
      
      const canvasMiddleX = canvas.width / 2;
      const canvasMiddleY = canvas.height / 2;
      
      setCarPosition(prev => {
        let newX = prev.x;
        let newY = prev.y;
        
        if (touchX < canvasMiddleX) {
          // Touch left side - move left
          newX = Math.max(0, prev.x - moveDelta);
        } else {
          // Touch right side - move right
          newX = Math.min(canvas.width - carWidth, prev.x + moveDelta);
        }
        
        if (touchY < canvasMiddleY) {
          // Touch top half - move up
          newY = Math.max(0, prev.y - moveDelta);
        } else {
          // Touch bottom half - move down
          newY = Math.min(canvas.height - carHeight, prev.y + moveDelta);
        }
        
        return { x: newX, y: newY };
      });
    };
    
    const handleCanvasClick = (e: MouseEvent) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      
      const rect = canvas.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const clickY = e.clientY - rect.top;
      
      // Check if click is on bubble icon
      if (collectedBubbles > 0 && !bubbleShieldActive) {
        const bubbleIconSize = Math.floor(40 * scale);
        const bubbleIconX = Math.floor(20 * scale);
        const bubbleIconY = Math.floor(140 * scale);
        
        if (clickX >= bubbleIconX && clickX <= bubbleIconX + bubbleIconSize &&
            clickY >= bubbleIconY - bubbleIconSize && clickY <= bubbleIconY) {
          // Activate bubble shield
          setCollectedBubbles(prev => prev - 1);
          setBubbleShieldActive(true);
          
          // Set 10 second timer
          if (bubbleShieldTimeoutRef.current) {
            clearTimeout(bubbleShieldTimeoutRef.current);
          }
          bubbleShieldTimeoutRef.current = setTimeout(() => {
            setBubbleShieldActive(false);
            bubbleShieldTimeoutRef.current = null;
          }, 10000);
        }
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    canvas.addEventListener('touchstart', handleTouch, { passive: false });
    canvas.addEventListener('click', handleCanvasClick);
    
    // Collision detection function
    const checkCollisions = () => {
      // Check bubble collection
      bubbles.forEach((bubble, bubbleIndex) => {
        const carLeft = carPosition.x;
        const carRight = carPosition.x + carWidth;
        const carTop = carPosition.y;
        const carBottom = carPosition.y + carHeight;
        
        const bubbleLeft = bubble.x;
        const bubbleRight = bubble.x + bubble.width;
        const bubbleTop = bubble.y;
        const bubbleBottom = bubble.y + bubble.height;
        
        if (carLeft < bubbleRight && carRight > bubbleLeft &&
            carTop < bubbleBottom && carBottom > bubbleTop) {
          // Bubble collected (only if player has less than 3 bubbles)
          setCollectedBubbles(prev => {
            if (prev < 3) {
              setBubbles(prevBubbles => prevBubbles.filter((_, i) => i !== bubbleIndex));
              return prev + 1;
            }
            return prev;
          });
        }
      });
      
      // Check obstacle collisions
      obstacles.forEach((obstacle, index) => {
        const carLeft = carPosition.x;
        const carRight = carPosition.x + carWidth;
        const carTop = carPosition.y;
        const carBottom = carPosition.y + carHeight;
        
        const obstacleLeft = obstacle.x;
        const obstacleRight = obstacle.x + obstacle.width;
        const obstacleTop = obstacle.y;
        const obstacleBottom = obstacle.y + obstacle.height;
        
        if (carLeft < obstacleRight && carRight > obstacleLeft &&
            carTop < obstacleBottom && carBottom > obstacleTop) {
          // Collision detected
          if (bubbleShieldActive) {
            // Shield protects from one hit
            setBubbleShieldActive(false);
            if (bubbleShieldTimeoutRef.current) {
              clearTimeout(bubbleShieldTimeoutRef.current);
              bubbleShieldTimeoutRef.current = null;
            }
            setObstacles(prev => prev.filter((_, i) => i !== index));
          } else {
            // Normal collision - lose a life
            const newLives = lives - 1;
            const heartSpacing = Math.floor(60 * scale);
            const heartX = canvas.width - Math.floor(50 * scale) - (newLives * heartSpacing);
            const heartY = Math.floor(40 * scale);
            
            // Create explosion at heart position
            const particles: Array<{
              x: number;
              y: number;
              vx: number;
              vy: number;
              life: number;
              maxLife: number;
            }> = [];
            for (let i = 0; i < 15; i++) {
              particles.push({
                x: heartX + 20,
                y: heartY - 10,
                vx: (Math.random() - 0.5) * 8,
                vy: (Math.random() - 0.5) * 8,
                life: 60,
                maxLife: 60
              });
            }
            
            setExplosions(prev => [...prev, { x: heartX, y: heartY, particles }]);
            setLives(prev => prev - 1);
            setObstacles(prev => prev.filter((_, i) => i !== index));
          }
        }
      });
    };

    // Game update loop
    const gameLoop = setInterval(() => {
      if (lives <= 0) return;
      
      // Update background scroll
      setBackgroundY(prev => prev + 3);
      
      // Update bubbles
      setBubbles(prev => {
        // Move bubbles down
        const updatedBubbles = prev.map(bubble => ({
          ...bubble,
          y: bubble.y + 3
        })).filter(bubble => bubble.y < canvas.height + 100);
        
        // Spawn bubbles randomly (less frequent than capybaras)
        const bubbleWidth = Math.floor(100 * scale);
        const bubbleHeight = Math.floor(100 * scale);
        
        const currentTime = Date.now();
        const timeSinceLastBubble = currentTime - lastBubbleSpawnTimeRef.current;
        
        if (Math.random() < 0.003 && 
            bubblesSpawnedThisLevel < 2 && 
            timeSinceLastBubble >= 10000 && 
            level >= 3) { // Don't generate bubbles in level 1 and 2
          updatedBubbles.push({
            x: Math.random() * (canvas.width - bubbleWidth),
            y: -bubbleHeight - Math.random() * 200,
            width: bubbleWidth,
            height: bubbleHeight,
          });
          setBubblesSpawnedThisLevel(prev => prev + 1);
          lastBubbleSpawnTimeRef.current = currentTime;
        }
        
        return updatedBubbles;
      });
      
      // Update obstacles
      setObstacles(prev => {
        // Move obstacles down
        const updatedObstacles = prev.map(obstacle => ({
          ...obstacle,
          y: obstacle.y + 4
        })).filter(obstacle => obstacle.y < canvas.height + 100);
        
        // Spawn capybaras randomly based on level (level 1 = 2 capybaras, level 2 = 3, etc.)
        const obstacleWidth = Math.floor(78 * scale);
        const obstacleHeight = Math.floor(98 * scale);
        const maxCapybarasForLevel = level + 1;
        
        // Random chance to spawn a single capybara - higher chance with higher levels
        const baseSpawnChance = 0.008;
        const levelMultiplier = 1 + (level - 1) * 0.3;
        const spawnChance = Math.min(baseSpawnChance * levelMultiplier, 0.03);
        
        if (Math.random() < spawnChance) {
          // Count current capybaras on screen
          const currentCapybarasCount = updatedObstacles.length;
          
          // Only spawn if we haven't reached the level's capybara limit
          if (currentCapybarasCount < maxCapybarasForLevel) {
            updatedObstacles.push({
              x: Math.random() * (canvas.width - obstacleWidth),
              y: -obstacleHeight - Math.random() * 200, // Random vertical offset
              width: obstacleWidth,
              height: obstacleHeight,
              isPink: Math.random() < 0.3, // 30% chance for pink capybara
            });
          }
        }
        
        return updatedObstacles;
      });
      
      // Update explosions
      setExplosions(prev => {
        return prev.map(explosion => ({
          ...explosion,
          particles: explosion.particles.map(particle => ({
            ...particle,
            x: particle.x + particle.vx,
            y: particle.y + particle.vy,
            life: particle.life - 1
          })).filter(particle => particle.life > 0)
        })).filter(explosion => explosion.particles.length > 0);
      });
      
      checkCollisions();
      draw();
    }, 16);

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
      canvas.removeEventListener('touchstart', handleTouch);
      canvas.removeEventListener('click', handleCanvasClick);
      clearInterval(gameLoop);
    };
  }, [gameStarted, carPosition, backgroundY, obstacles, bubbles, lives, explosions, canvasSize, score, bubbleShieldActive]);

  // Separate useEffect for score timer
  useEffect(() => {
    if (!gameStarted || lives <= 0) return;

    const scoreTimer = setInterval(() => {
      setScore(prev => prev + 1);
    }, 1000);

    return () => {
      clearInterval(scoreTimer);
    };
  }, [gameStarted, lives]);

  // Level progression timer - increase level every 30 seconds
  useEffect(() => {
    if (!gameStarted || lives <= 0) return;

    const levelProgressTimer = setInterval(() => {
      const elapsedSeconds = Math.floor((Date.now() - gameStartTimeRef.current) / 1000);
      const newLevel = Math.floor(elapsedSeconds / 30) + 1;
      setLevel(prevLevel => {
        if (newLevel !== prevLevel) {
          // Reset bubble spawn counter and timer for new level
          setBubblesSpawnedThisLevel(0);
          lastBubbleSpawnTimeRef.current = 0;
        }
        return newLevel;
      });
    }, 1000);

    return () => {
      clearInterval(levelProgressTimer);
    };
  }, [gameStarted, lives]);

  return (
    <div className="App">
      <header className="App-header">
        {(!gameStarted || lives <= 0) && (
          <button
            onClick={() => {
              setShowInstructions(true);
              setInstructionsOpenedWithTouch(false);
            }}
            onTouchStart={() => {
              setShowInstructions(true);
              setInstructionsOpenedWithTouch(true);
            }}
            style={{
              position: 'absolute',
              top: '20px',
              left: '20px',
              padding: '10px 15px',
              fontSize: '14px',
              backgroundColor: '#2196F3',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              zIndex: 1001
            }}
          >
            Instructions
          </button>
        )}
        <h1>Maia's Racing Game</h1>
        {!gameStarted || lives <= 0 ? (
          <div>
            {lives <= 0 && (
              <div>
                <p style={{ color: 'red', fontSize: '24px' }}>Game Over!</p>
                <p style={{ color: 'white', fontSize: '20px' }}>Final Score: {score}</p>
                {!scoreSubmitted && (
                  <div style={{ margin: '20px 0' }}>
                    <input
                      type="text"
                      placeholder="Enter your name"
                      value={playerName}
                      onChange={(e) => setPlayerName(e.target.value)}
                      style={{
                        padding: '10px',
                        fontSize: '16px',
                        borderRadius: '5px',
                        border: '1px solid #ccc',
                        marginRight: '10px',
                        width: '200px'
                      }}
                      disabled={isSubmittingScore}
                    />
                    <button
                      onClick={submitScore}
                      disabled={isSubmittingScore || !playerName.trim()}
                      style={{
                        padding: '10px 20px',
                        fontSize: '16px',
                        backgroundColor: isSubmittingScore || !playerName.trim() ? '#ccc' : '#2196F3',
                        color: 'white',
                        border: 'none',
                        borderRadius: '5px',
                        cursor: isSubmittingScore || !playerName.trim() ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {isSubmittingScore ? 'Submitting...' : 'Submit Score'}
                    </button>
                  </div>
                )}
                {showLeaderboard && (
                  <div style={{ 
                    marginTop: '20px', 
                    padding: '20px', 
                    backgroundColor: '#f0f0f0', 
                    borderRadius: '10px',
                    maxWidth: '400px',
                    margin: '20px auto'
                  }}>
                    {isNewHighScore && (
                      <div style={{
                        backgroundColor: '#FFD700',
                        color: '#000',
                        padding: '10px',
                        borderRadius: '5px',
                        marginBottom: '15px',
                        fontSize: '20px',
                        fontWeight: 'bold',
                        textAlign: 'center',
                        animation: 'pulse 2s infinite'
                      }}>
                        🎉 New High Score! 🎉
                      </div>
                    )}
                    <h3 style={{ color: '#333', marginBottom: '15px' }}>🏆 Leaderboard</h3>
                    {isLoadingLeaderboard ? (
                      <p style={{ color: '#666' }}>Loading leaderboard...</p>
                    ) : leaderboard.length > 0 ? (
                      <div style={{ textAlign: 'left' }}>
                        {leaderboard.slice(0, 10).map((entry, index) => (
                          <div key={index} style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            padding: '8px 0',
                            borderBottom: index < 9 ? '1px solid #ddd' : 'none',
                            color: '#333'
                          }}>
                            <span>
                              <strong>#{index + 1}</strong> {entry.Name}
                            </span>
                            <span><strong>{entry.Score}</strong></span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p style={{ color: '#666' }}>No scores yet!</p>
                    )}
                    <button
                      onClick={() => setShowLeaderboard(false)}
                      style={{
                        marginTop: '15px',
                        padding: '8px 16px',
                        fontSize: '14px',
                        backgroundColor: '#666',
                        color: 'white',
                        border: 'none',
                        borderRadius: '5px',
                        cursor: 'pointer'
                      }}
                    >
                      Hide Leaderboard
                    </button>
                  </div>
                )}
              </div>
            )}
            <button 
              onClick={handleStart}
              style={{
                padding: '15px 30px',
                marginBottom: '30px',
                fontSize: '18px',
                backgroundColor: '#4CAF50',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer'
              }}
            >
              {lives <= 0 ? 'Restart' : 'Start'}
            </button>
          </div>
        ) : (
          <canvas 
            ref={canvasRef}
            width={canvasSize.width}
            height={canvasSize.height}
            style={{ 
              border: isMobile() ? 'none' : '2px solid #fff', 
              backgroundColor: '#333',
              display: 'block',
              ...(isMobile() ? {
                position: 'fixed',
                top: 0,
                left: 0,
                zIndex: 1000
              } : {})
            }}
          />
        )}
        {showInstructions && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 2000
          }}>
            <div style={{
              backgroundColor: 'white',
              padding: '30px',
              borderRadius: '10px',
              maxWidth: '500px',
              maxHeight: '80%',
              overflow: 'auto',
              position: 'relative',
              margin: '20px'
            }}>
              <button
                onClick={() => setShowInstructions(false)}
                style={{
                  position: 'absolute',
                  top: '10px',
                  right: '10px',
                  width: '30px',
                  height: '30px',
                  backgroundColor: '#ff4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  fontSize: '18px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
              <h2 style={{ color: '#333', marginBottom: '20px' }}>How to Play</h2>
              <div style={{ color: '#333', lineHeight: '1.6' }}>
                <h3>🎯 Objective</h3>
                <p>Avoid the capybaras and survive as long as possible to get a high score!</p>
                
                <h3>🎮 Controls</h3>
                {isMobileDevice() || instructionsOpenedWithTouch ? (
                  <>
                    <h4>📱 Touch Controls</h4>
                    <ul>
                      <li><strong>Move:</strong> Tap the left/right side of the screen to move left/right</li>
                      <li><strong>Move:</strong> Tap the top/bottom half to move up/down</li>
                      <li><strong>Use Bubble:</strong> Tap the bubble icon when you have bubbles collected</li>
                    </ul>
                    {!isMobileDevice() && instructionsOpenedWithTouch && (
                      <>
                        <h4>⌨️ Keyboard Controls</h4>
                        <ul>
                          <li><strong>Move:</strong> Use arrow keys (↑↓←→) to move your car</li>
                          <li><strong>Use Bubble:</strong> Press SPACEBAR or click the bubble icon</li>
                        </ul>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <h4>⌨️ Keyboard Controls</h4>
                    <ul>
                      <li><strong>Move:</strong> Use arrow keys (↑↓←→) to move your car</li>
                      <li><strong>Use Bubble:</strong> Press SPACEBAR or click the bubble icon</li>
                    </ul>
                  </>
                )}
                
                <h3>💙 Bubble Protection</h3>
                <ul>
                  <li>Collect bubbles by driving into them (available from Level 3+)</li>
                  <li>You can hold up to 3 bubbles maximum</li>
                  <li>Activate bubble shield to protect from 1 capybara hit</li>
                  <li>Shield lasts 10 seconds or until hit by a capybara</li>
                </ul>
                
                <h3>📈 Scoring</h3>
                <ul>
                  <li>+1 point every second you survive</li>
                  <li>Level increases every 30 seconds</li>
                  <li>More capybaras appear as levels increase</li>
                </ul>
                
                <h3>❤️ Lives</h3>
                <p>You start with 3 lives. Lose a life when hit by a capybara (unless protected by bubble shield).</p>
              </div>
            </div>
          </div>
        )}
      </header>
    </div>
  );
}

export default App;
